import './laxhjalpen-guardian-participation-page.scss';
import type { CSSProperties } from 'react';
import { BizIcon } from '../../../../../FrontEndReact/src/Core/Components/Mini/BizIcon';
import { getPathWithRootPath } from '../../../../../FrontEndReact/src/Core/Services/common';
import { useFrontendState } from '../../../../../FrontEnd/Common/frontendStateContext';
import {
	LhStudentParticipationStats,
	useGetRelatedStudentParticipationStatsToLoggedInGuardian,
} from '../Services/laxhjalpenUserParticipation';

const LaxhjalpenGuardianParticipationPage = () => {
	const { state } = useFrontendState();
	const isGuardian = state.user?.Roles?.some((role) => role.SystemName === 'LH_GUARDIAN') ?? false;
	const { data, statusState } = useGetRelatedStudentParticipationStatsToLoggedInGuardian(state.user?.Id, isGuardian);

	if (!state.user || statusState === 'LOADING') {
		return (
			<div className='biz-lh-guardian-participation-page biz-lh-guardian-participation-page--loading'>
				<BizIcon icon='LOADING' />
				<span>Laddar deltagande...</span>
			</div>
		);
	}

	if (!isGuardian) {
		return (
			<div className='biz-lh-guardian-participation-page biz-lh-guardian-participation-page--error'>
				Du har inte behörighet att se elevstatistik.
			</div>
		);
	}

	if (statusState === 'ERROR') {
		return (
			<div className='biz-lh-guardian-participation-page biz-lh-guardian-participation-page--error'>
				Det gick inte att hämta deltagandet just nu.
			</div>
		);
	}

	if (!data || data.length === 0) {
		return (
			<div className='biz-lh-guardian-participation-page biz-lh-guardian-participation-page--empty'>
				Ingen kopplad elevstatistik hittades.
			</div>
		);
	}

	return (
		<section className='biz-lh-guardian-participation-page'>
			<div className='biz-lh-guardian-participation-page__list'>
				{data.map((student) => (
					<StudentParticipationCard key={student.userId} student={student} />
				))}
			</div>
		</section>
	);
};

export default LaxhjalpenGuardianParticipationPage;

function StudentParticipationCard({ student }: { student: LhStudentParticipationStats }) {
	const attendancePercent = getPercent(student.attendedCount, student.eventCount);
	const approvedAbsencePercent = getPercent(student.approvedAbsenceCount, student.eventCount);
	const notApprovedAbsencePercent = getPercent(student.notApprovedAbsenceCount, student.eventCount);
	const metrics = [
		{
			label: 'Narvaro',
			displayLabel: 'Närvaro',
			percent: attendancePercent,
			count: student.attendedCount,
			color: '#3A7ED9',
		},
		{
			label: 'Giltig franvaro',
			displayLabel: 'Giltig frånvaro',
			percent: approvedAbsencePercent,
			count: student.approvedAbsenceCount,
			color: '#9BA3AF',
		},
		{
			label: 'Ogiltig franvaro',
			displayLabel: 'Ogiltig frånvaro',
			percent: notApprovedAbsencePercent,
			count: student.notApprovedAbsenceCount,
			color: '#D1D5DC',
		},
	];
	const photoUrl = getUserPhotoUrl(student);
	const displayName = student.user
		? `${student.user.firstName ?? ''} ${student.user.lastName ?? ''}`.trim() || student.user.userName
		: student.userName;

	return (
		<article className='biz-lh-guardian-participation-page__card'>
			<div className='biz-lh-guardian-participation-page__card-top'>
				<div className='biz-lh-guardian-participation-page__avatar'>
					{photoUrl ? (
						<img src={photoUrl} alt={displayName} />
					) : (
						<span>{getInitials(displayName)}</span>
					)}
				</div>
				<div>
					<h2 className='biz-lh-guardian-participation-page__card-title'>{displayName}</h2>
					<div className='biz-lh-guardian-participation-page__card-meta-block'>
						<div className='biz-lh-guardian-participation-page__card-meta'>
							<div className='biz-lh-guardian-participation-page__card-meta-text'>Grupp {student.unit?.name ? `${student.unit.name}` : ''}</div>
						</div>
					</div>
				</div>
			</div>

			<div className='biz-lh-guardian-participation-page__metrics'>
				{metrics.map((metric) => {
					const metricStyle = {
						'--biz-lh-metric-color': metric.color,
						'--biz-lh-metric-progress': `${metric.percent}%`,
					} as CSSProperties;

					return (
						<div key={metric.label} className='biz-lh-guardian-participation-page__metric' style={metricStyle}>
						<span className='biz-lh-guardian-participation-page__metric-label'>
							<span className='biz-lh-guardian-participation-page__metric-dot' aria-hidden='true'></span>
							{metric.displayLabel}
						</span>
						<strong className='biz-lh-guardian-participation-page__metric-value'>{metric.percent}%</strong>
						<span className='biz-lh-guardian-participation-page__metric-meta'>
							{metric.count} av {student.eventCount} pass
						</span>
						<span className='biz-lh-guardian-participation-page__metric-progress' aria-hidden='true'>
							<span className='biz-lh-guardian-participation-page__metric-progress-fill'></span>
						</span>
					</div>
					);
				})}
			</div>
		</article>
	);
}

function getPercent(value: number, total: number) {
	if (total <= 0) return 0;
	return Math.round((value / total) * 100);
}

function getUserPhotoUrl(student: LhStudentParticipationStats) {
	const photo = student.user?.photo;
	if (!photo) return '';

	return getPathWithRootPath(photo, 'sm') || '';
}

function getInitials(name: string) {
	const parts = name.split(' ').filter(Boolean);
	if (parts.length <= 0) return '?';
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}